import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { listWorkEfforts, createWorkEffort, deleteWorkEffort } from '../../api'
import './WorkEffortsTab.css'

const STATE_META = {
  empty:         { label: 'Draft',      cls: 'draft',    icon: '○' },
  plan:          { label: 'Planning',   cls: 'planning', icon: '◎' },
  running:       { label: 'Running',    cls: 'running',  icon: '▶' },
  clarification: { label: 'Waiting',    cls: 'waiting',  icon: '?' },
  error:         { label: 'Error',      cls: 'error',    icon: '!' },
  complete:      { label: 'Complete',   cls: 'complete', icon: '✓' },
  export:        { label: 'Exported',   cls: 'exported', icon: '↓' },
}

function fmtDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  const now = new Date()
  const diffMs = now - d
  const diffMins = Math.floor(diffMs / 60000)
  const diffHrs = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)
  if (diffMins < 2)  return 'just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHrs < 24)  return `${diffHrs}h ago`
  if (diffDays < 7)  return `${diffDays}d ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function fmtDuration(ms) {
  if (!ms) return null
  const mins = Math.round(ms / 60000)
  if (mins < 60) return `${mins}m`
  return `${Math.floor(mins / 60)}h ${mins % 60}m`
}

// ─── Single effort card ──────────────────────────────────
function EffortCard({ effort, customerId, onDelete }) {
  const navigate = useNavigate()
  const [deleting, setDeleting] = useState(false)
  const meta = STATE_META[effort.state] || STATE_META.empty

  const stats = effort.state_data || {}
  const findingCount  = stats.findings?.length ?? effort.finding_count ?? null
  const actionCount   = stats.actions?.length ?? effort.action_count ?? null
  const stepCount     = stats.steps?.length ?? null
  const doneSteps     = stats.steps?.filter(s => s.status === 'complete').length ?? null

  const handleDelete = async (e) => {
    e.stopPropagation()
    if (!window.confirm(`Delete "${effort.title}"? This cannot be undone.`)) return
    setDeleting(true)
    try { await deleteWorkEffort(effort.id) }
    catch (err) { console.error('Delete failed:', err) }
    onDelete(effort.id)
  }

  return (
    <div
      className={`wet-card wet-card-${meta.cls}`}
      onClick={() => navigate(`/customers/${customerId}/work-efforts/${effort.id}`)}
    >
      <div className={`wet-state-bar wet-sb-${meta.cls}`} />
      <div className="wet-card-main">
        <div className="wet-card-top">
          <span className={`wet-state-tag wet-stag-${meta.cls}`}>
            {meta.icon} {meta.label}
          </span>
          <span className="wet-card-date" title={effort.updated_at}>{fmtDate(effort.updated_at)}</span>
        </div>
        <div className="wet-card-title">{effort.title || 'Untitled Investigation'}</div>
        {effort.description && (
          <div className="wet-card-desc">{effort.description}</div>
        )}
        <div className="wet-card-meta">
          {stepCount != null && doneSteps != null && (
            <span className="wet-meta-pill">{doneSteps}/{stepCount} steps</span>
          )}
          {findingCount != null && findingCount > 0 && (
            <span className="wet-meta-pill wet-mp-findings">
              {findingCount} finding{findingCount !== 1 ? 's' : ''}
            </span>
          )}
          {actionCount != null && actionCount > 0 && (
            <span className="wet-meta-pill wet-mp-actions">
              {actionCount} action{actionCount !== 1 ? 's' : ''}
            </span>
          )}
          {effort.duration_ms && (
            <span className="wet-meta-pill">{fmtDuration(effort.duration_ms)}</span>
          )}
          {effort.tags?.length > 0 && effort.tags.slice(0, 2).map(tag => (
            <span key={tag} className="wet-meta-pill wet-mp-tag">{tag}</span>
          ))}
        </div>
      </div>
      <div className="wet-card-actions">
        <button
          className="wet-open-btn"
          onClick={e => { e.stopPropagation(); navigate(`/customers/${customerId}/work-efforts/${effort.id}`) }}
        >
          Open →
        </button>
        <button
          className="wet-delete-btn"
          onClick={handleDelete}
          disabled={deleting}
          title="Delete"
        >
          {deleting ? '…' : '✕'}
        </button>
      </div>
    </div>
  )
}

// ─── Empty state ─────────────────────────────────────────
function EmptyState({ customerId, onCreate }) {
  const suggestions = [
    'Compare payroll config to compliance rules',
    'Audit benefit eligibility across all employees',
    'Analyze termination patterns by quarter',
    'Check state wage & hour compliance',
    'Identify deduction gaps before go-live',
    'Validate ACA measurement period setup',
  ]
  return (
    <div className="wet-empty">
      <div className="wet-empty-icon">⚡</div>
      <div className="wet-empty-title">No investigations yet</div>
      <div className="wet-empty-sub">
        Start an investigation to analyze data, surface findings, and generate a remediation plan.
      </div>
      <div className="wet-suggestions">
        {suggestions.map(s => (
          <button key={s} className="wet-suggestion-chip" onClick={() => onCreate(s)}>
            {s}
          </button>
        ))}
      </div>
    </div>
  )
}

// ═════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════
export default function WorkEffortsTab({ customerId }) {
  const navigate = useNavigate()
  const [efforts, setEfforts] = useState([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [stateFilter, setStateFilter] = useState('all')

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const data = await listWorkEfforts(customerId)
        if (!cancelled) setEfforts(Array.isArray(data) ? data : (data?.efforts || []))
      } catch (err) {
        console.error('Failed to load work efforts:', err)
        if (!cancelled) setEfforts([])
      }
      if (!cancelled) setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [customerId])

  const handleCreate = async (prefill = '') => {
    setCreating(true)
    try {
      const effort = await createWorkEffort(customerId, 'New Investigation')
      if (effort?.id) {
        const url = `/customers/${customerId}/work-efforts/${effort.id}${prefill ? `?q=${encodeURIComponent(prefill)}` : ''}`
        navigate(url)
      }
    } catch (err) {
      console.error('Failed to create work effort:', err)
    }
    setCreating(false)
  }

  const handleDelete = (effortId) => {
    setEfforts(prev => prev.filter(e => e.id !== effortId))
  }

  // Filter
  const filtered = stateFilter === 'all'
    ? efforts
    : efforts.filter(e => {
        if (stateFilter === 'active') return ['running', 'plan', 'clarification'].includes(e.state)
        if (stateFilter === 'complete') return ['complete', 'export'].includes(e.state)
        if (stateFilter === 'draft') return !e.state || e.state === 'empty'
        return e.state === stateFilter
      })

  // Counts for filter chips
  const activeCount   = efforts.filter(e => ['running', 'plan', 'clarification'].includes(e.state)).length
  const completeCount = efforts.filter(e => ['complete', 'export'].includes(e.state)).length
  const draftCount    = efforts.filter(e => !e.state || e.state === 'empty').length

  return (
    <div className="wet-root">
      {/* Header row */}
      <div className="wet-header">
        <div className="wet-header-left">
          <div className="wet-header-title">Work Efforts</div>
          <div className="wet-header-sub">
            {efforts.length} investigation{efforts.length !== 1 ? 's' : ''} for this customer
          </div>
        </div>
        <button
          className="wet-new-btn"
          onClick={() => handleCreate()}
          disabled={creating}
        >
          {creating ? 'Creating…' : '+ Start New Investigation'}
        </button>
      </div>

      {/* Filter bar */}
      {efforts.length > 0 && (
        <div className="wet-filter-bar">
          {[
            { key: 'all',      label: `All ${efforts.length}` },
            { key: 'active',   label: `▶ Active ${activeCount}`,   hidden: activeCount === 0 },
            { key: 'complete', label: `✓ Complete ${completeCount}`,hidden: completeCount === 0 },
            { key: 'draft',    label: `○ Draft ${draftCount}`,      hidden: draftCount === 0 },
            { key: 'error',    label: `! Error`, hidden: !efforts.some(e => e.state === 'error') },
          ].filter(f => !f.hidden).map(f => (
            <button
              key={f.key}
              className={`wet-filter-chip ${stateFilter === f.key ? 'active' : ''}`}
              onClick={() => setStateFilter(f.key)}
            >
              {f.label}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="wet-loading">Loading investigations…</div>
      ) : efforts.length === 0 ? (
        <EmptyState customerId={customerId} onCreate={handleCreate} />
      ) : filtered.length === 0 ? (
        <div className="wet-no-results">No {stateFilter} investigations.</div>
      ) : (
        <div className="wet-list">
          {filtered.map(effort => (
            <EffortCard
              key={effort.id}
              effort={effort}
              customerId={customerId}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  )
}
